import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, AlertCircle, RotateCcw, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type ImportState } from '@/pages/Import';

interface ImportResultsProps {
  results: NonNullable<ImportState['results']>;
  onStartOver: () => void;
}

export function ImportResults({ results, onStartOver }: ImportResultsProps) {
  const { successful, failed, errors } = results;
  const total = successful + failed;
  const successRate = total > 0 ? (successful / total) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Success/Failure Summary */}
      <div className="text-center space-y-4">
        {successful > 0 && failed === 0 ? (
          <div className="flex justify-center">
            <CheckCircle2 className="h-16 w-16 text-green-500" />
          </div>
        ) : failed > 0 && successful === 0 ? (
          <div className="flex justify-center">
            <AlertCircle className="h-16 w-16 text-red-500" />
          </div>
        ) : (
          <div className="flex justify-center">
            <AlertCircle className="h-16 w-16 text-yellow-500" />
          </div>
        )}

        <div>
          <h2 className="text-2xl font-bold">
            {successful > 0 && failed === 0 
              ? 'Import Completed Successfully!' 
              : failed > 0 && successful === 0
              ? 'Import Failed'
              : 'Import Partially Completed'
            }
          </h2>
          <p className="text-gray-600 mt-2">
            {successful > 0 && failed === 0 
              ? `${successful} prospects were successfully imported into your pipeline.`
              : failed > 0 && successful === 0
              ? 'No prospects were imported due to errors.'
              : `${successful} prospects imported successfully, ${failed} failed.`
            }
          </p>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-600">{successful}</p>
                <p className="text-sm text-gray-500">Successful</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-red-600">{failed}</p>
                <p className="text-sm text-gray-500">Failed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-2">
              <div className="h-5 w-5 rounded-full bg-blue-500" />
              <div>
                <p className="text-2xl font-bold text-blue-600">{successRate.toFixed(1)}%</p>
                <p className="text-sm text-gray-500">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Details */}
      {errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span>Import Errors</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {errors.map((error, index) => (
                <div key={index} className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-medium text-red-800">
                    {error.row > 0 ? `Row ${error.row}: ` : ''}{error.message}
                  </p>
                  {error.field && (
                    <p className="text-xs text-red-600 mt-1">
                      Field: {error.field}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      {successful > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Your prospects have been imported and are ready for management:
              </p>
              <div className="flex flex-wrap gap-3">
                <Button asChild variant="outline">
                  <Link to="/pipeline">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Pipeline
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/prospects">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Manage Prospects
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/dashboard">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4">
        <Button onClick={onStartOver} variant="outline">
          <RotateCcw className="h-4 w-4 mr-2" />
          Import Another File
        </Button>
        
        {successful > 0 && (
          <Button asChild>
            <Link to="/pipeline">
              <ExternalLink className="h-4 w-4 mr-2" />
              Go to Pipeline
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
}